/** What the map's filters currently keep: how many trees and how much timber. */
import { useMemo } from 'react';
import { Box, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useFormat } from '@/components/useFormat';
import type { LayerFilters } from '@/map/layers';
import { MAP } from '@/theme/colors';
import { brand } from '@/theme/theme';
import { MAP_FRAME_FREE } from '../MapFrame';
import { passesFilters, type TreeExtras } from './extras';
import type { TreeStats } from './stats';

export function SelectionBox({ stats, extras, filters }: { stats: TreeStats; extras: Map<string, TreeExtras>; filters?: LayerFilters }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const { count, volume } = useMemo(() => {
    let count = 0;
    let volume = 0;
    for (const tree of stats.trees) {
      if (!passesFilters(tree, extras.get(tree.id), filters)) continue;
      count += 1;
      volume += tree.volume ?? 0;
    }
    // Points from a thinned tile zoom stand for `sample` trees each.
    const k = stats.derived?.sample ?? 1;
    return { count: count * k, volume: volume * k };
  }, [stats, extras, filters]);
  const approximate = (stats.derived?.sample ?? 1) > 1;
  return (
    <Box
      data-testid="selection-box"
      pos="absolute"
      top={MAP_FRAME_FREE.top}
      left={MAP_FRAME_FREE.left}
      px={10}
      py={6}
      style={{ zIndex: 2, backgroundColor: MAP.controlBg, border: `1px solid ${MAP.controlBorder}`, borderRadius: 8 }}
    >
      <Text fz={11} fw={700} tt="uppercase" c={brand.textMuted}>
        {t('trees.selection.title')}
      </Text>
      <Text fz={13} c={brand.text}>
        {t(approximate ? 'trees.selection.bodyApprox' : 'trees.selection.body', { count: number(count), volume: number(volume, 1) })}
      </Text>
    </Box>
  );
}
